import {
	SceneTransform,
	Settings,
	LeftPanelTab,
	Scaling,
	DsTab,
} from 'redux/mode/reducers';
import { Command } from 'redux/mode/commands';
import { Attachment, ChatMode, DsPick } from 'dragonmouse-shared/interfaces';
import { TokenCategory, ComponentCategory, DsViewPreferences } from 'designSystem/types';
import { DsLinkState } from 'hooks/useDsLinkState';

// Data that should persist across browser sessions and be shared across tabs
export interface LocalData {
	transforms?: { [sceneId: string]: SceneTransform };
	leftPanelWidths?: { [tab in LeftPanelTab]: number };
	rightPanelWidth?: number;
	designSystemChatPanelWidth?: number;
	settings?: Settings;
	prototypeScaling?: Scaling;
	sidebarVisible?: boolean;
	sidebarVisibilityInitialized?: boolean;
	sidebarDocked?: boolean;
	designSystemFilters?: {
		theme: {
			selectedCategories: TokenCategory[];
		};
		styles: {};
		components: {
			selectedCategories: ComponentCategory[];
		};
		gallery: {};
		assets: {};
	};
	// Design system tabs keyed by DS ID for persistence across sessions.
	// When opening a fresh browser tab/session, tabs are seeded from this storage.
	// See SessionData.dsTabsByDesignSystem for the runtime per-browser-tab storage.
	dsTabsByDesignSystem?: {
		[designSystemId: string]: {
			tabs: DsTab[];
			activeTabId: string | null;
		};
	};
	dsViewPreferencesByDesignSystem?: {
		[designSystemId: string]: DsViewPreferences;
	};
}

// Data that should be per-tab and not persist across browser sessions
export interface SessionData {
	activeSceneChatId?: string | null;
	activeLeftPanelTab?: LeftPanelTab;
	selectedFrameIds?: string[];
	commandStack?: Command[];
	undoneCommands?: Command[];
	centeredFrameId?: string | null;
	trackedUserIds?: string[];
	panelsVisible?: boolean;
	// Design system tabs keyed by DS ID for per-browser-tab runtime state.
	// Each browser tab maintains independent tabs during its session.
	// On initial load, tabs are seeded from LocalData.dsTabsByDesignSystem if this is empty.
	// Changes are saved to both session (for independence) and local (for persistence).
	dsTabsByDesignSystem?: {
		[designSystemId: string]: {
			tabs: DsTab[];
			activeTabId: string | null;
		};
	};
	dsPicksByDesignSystem?: { [designSystemId: string]: DsPick[] };
	selectedFrameIdsByScene?: { [sceneId: string]: string[] };
	dismissedDsBannerByDs?: Record<string, { state: DsLinkState; versionNumber: number }>;
	commentIntroShownForScenes?: string[];
	pendingChatInputs?: { [chatId: string]: { message: string; chatMode: ChatMode; frameIds?: string[]; attachments?: Attachment[] } };
	pendingDsChatInputs?: { [dsChatId: string]: { message: string; chatMode: ChatMode; frameIds?: string[]; attachments?: Attachment[] } };
}

// Core idea here is that storing stuff long-term can be handled here, since there
// is some interplay between parts of this. Could switch more stuff to this pattern,
// which allows us to do a simple get / set when using it and change the way it's
// stored without changing the rest of the code.
class Storage {
	conduitInitialized: boolean = false;
	sceneFramesRetrieved: boolean = false;

	get local(): LocalData {
		const localData: LocalData = {};

		const jsonText = localStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
				// TODO: consider using lodash's merge to make this more compact - https://lodash.com/docs/4.17.15#merge
				// since merge doesn't seem to deal with the Number.isFinite cases above, I'm not adding it just yet...
				if (saved.transforms !== undefined) {
					localData.transforms = { ...saved.transforms };
				}
				if (saved.leftPanelWidths !== undefined) {
					localData.leftPanelWidths = { ...saved.leftPanelWidths };
				}
				if (saved.rightPanelWidth !== undefined) {
					localData.rightPanelWidth = saved.rightPanelWidth;
				}
				if (saved.designSystemChatPanelWidth !== undefined) {
					localData.designSystemChatPanelWidth = saved.designSystemChatPanelWidth;
				}
				if (saved.settings !== undefined) {
					localData.settings = { ...saved.settings };
				}
				if (saved.prototypeScaling !== undefined) {
					localData.prototypeScaling = saved.prototypeScaling;
				}
				if (saved.sidebarVisible !== undefined) {
					localData.sidebarVisible = saved.sidebarVisible;
				}
				if (saved.sidebarVisibilityInitialized !== undefined) {
					localData.sidebarVisibilityInitialized = saved.sidebarVisibilityInitialized;
				}
				if (saved.sidebarDocked !== undefined) {
					localData.sidebarDocked = saved.sidebarDocked;
				}
				if (saved.designSystemFilters !== undefined) {
					localData.designSystemFilters = { ...saved.designSystemFilters };
				}
				if (saved.dsTabsByDesignSystem !== undefined) {
					// Deep clone to avoid shared references to nested tabs arrays
					localData.dsTabsByDesignSystem = Object.fromEntries(
						Object.entries(saved.dsTabsByDesignSystem).map(([key, value]) => [
							key,
							{
								tabs: [...(value as { tabs: DsTab[] }).tabs],
								activeTabId: (value as { activeTabId: string | null }).activeTabId,
							},
						])
					);
				}
				if (saved.dsViewPreferencesByDesignSystem !== undefined) {
					localData.dsViewPreferencesByDesignSystem = { ...saved.dsViewPreferencesByDesignSystem };
				}
			}
		}
		return localData;
	}

	set local(data: LocalData) {
		localStorage.setItem('mode', JSON.stringify(data));
	}

	get session(): SessionData {
		const sessionData: SessionData = {};

		const jsonText = sessionStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
			if (saved.activeSceneChatId !== undefined) {
				sessionData.activeSceneChatId = saved.activeSceneChatId;
			}
			if (saved.selectedFrameIds !== undefined) {
				sessionData.selectedFrameIds = [...saved.selectedFrameIds];
			}
			if (saved.activeLeftPanelTab !== undefined) {
					sessionData.activeLeftPanelTab = saved.activeLeftPanelTab;
				}
				if (saved.commandStack !== undefined) {
					sessionData.commandStack = [...saved.commandStack];
				}
				if (saved.undoneCommands !== undefined) {
					sessionData.undoneCommands = [...saved.undoneCommands];
				}
				if (saved.centeredFrameId !== undefined) {
					sessionData.centeredFrameId = saved.centeredFrameId;
				}
				if (saved.trackedUserIds !== undefined) {
					sessionData.trackedUserIds = [...saved.trackedUserIds];
				}
				if (saved.panelsVisible !== undefined) {
					sessionData.panelsVisible = saved.panelsVisible;
				}
				if (saved.dsTabsByDesignSystem !== undefined) {
					// Deep clone to avoid shared references to nested tabs arrays
					sessionData.dsTabsByDesignSystem = Object.fromEntries(
						Object.entries(saved.dsTabsByDesignSystem).map(([key, value]) => [
							key,
							{
								tabs: [...(value as { tabs: DsTab[] }).tabs],
								activeTabId: (value as { activeTabId: string | null }).activeTabId,
							},
						])
					);
				}
				if (saved.dsPicksByDesignSystem !== undefined && typeof saved.dsPicksByDesignSystem === 'object' && !Array.isArray(saved.dsPicksByDesignSystem)) {
					sessionData.dsPicksByDesignSystem = { ...saved.dsPicksByDesignSystem };
				}
				if (saved.selectedFrameIdsByScene !== undefined && typeof saved.selectedFrameIdsByScene === 'object') {
					sessionData.selectedFrameIdsByScene = { ...saved.selectedFrameIdsByScene };
				}
				if (saved.dismissedDsBannerByDs !== undefined) {
					sessionData.dismissedDsBannerByDs = { ...saved.dismissedDsBannerByDs };
				}
			if (saved.commentIntroShownForScenes !== undefined) {
				sessionData.commentIntroShownForScenes = [...saved.commentIntroShownForScenes];
			}
			if (saved.pendingChatInputs !== undefined) {
				sessionData.pendingChatInputs = { ...saved.pendingChatInputs };
			}
			if (saved.pendingDsChatInputs !== undefined) {
				sessionData.pendingDsChatInputs = { ...saved.pendingDsChatInputs };
			}
			}
		}
		return sessionData;
	}

	set session(data: SessionData) {
		sessionStorage.setItem('mode', JSON.stringify(data));
	}
}

export default new Storage();
